import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressBar } from 'primereact/progressbar';
import axios from "axios";
import { Badge } from 'primereact/badge';

const DashboardTemplateFormComponent = (props) => {
  const { t } = props;
  const { action, id } = useParams();
  const history = useHistory();

  const [template, setTemplate] = useState(null);
  const [availableElements, setAvailableElements] = useState({ layouts: [], charts: [], kpis: [] });
  const [loading, setLoading] = useState(true);

  const apiCall = useCallback((method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const elementsRes = await apiCall('get', '/api/v2/dashboard-template/layout-elements/project');
        setAvailableElements(elementsRes.data.data);

        if (action === 'update' && id) {
          const templateRes = await apiCall('get', `/api/v2/dashboard-template/${id}`);
          setTemplate(templateRes.data.data);
        } else {
          setTemplate({
            name: t('dashboardTemplateDefaultName'),
            code: 'new_dashboard_' + Math.floor(Math.random() * 1000),
            owner_type: 'project',
            cooldown_seconds: 60,
            is_active: true,
            layout: { type: 'verticalLayout', elements: [] }
          });
        }
      } catch (error) {
        window.App.toastShow('error', t('error'), error.toString());
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, action, apiCall, t]);

  
  const typeOptions = [
    { label: t('dashboardTemplateVerticalLayout'), value: 'verticalLayout' },
    { label: t('dashboardTemplateHorizontalLayout'), value: 'horizontalLayout' },
    { label: t('dashboardTemplateKpiCard'), value: 'kpi' },
    { label: t('dashboardTemplateBarChart'), value: 'bar' },
    { label: t('dashboardTemplatePieChart'), value: 'pie' },
    { label: t('dashboardTemplateTable'), value: 'table' }
  ];

  const getTypeBadge = (type) => {
    switch (type) {
      case 'verticalLayout':
        return <Badge value="Vertical" severity="info" />;
      case 'horizontalLayout':
        return <Badge value="Horizontal" severity="warning" />;
      case 'kpi':
        return <Badge value="KPI" severity="success" />;
      case 'bar':
        return <Badge value="Bar" severity="help" />;
      case 'pie':
        return <Badge value="Pie" severity="warning" />;
      case 'table':
        return <Badge value="Table" severity="contrast" />;
      default:
        return <Badge value={type || t('dashboardTemplateElement')} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'verticalLayout':
        return t('dashboardTemplateVerticalLayout');
      case 'horizontalLayout':
        return t('dashboardTemplateHorizontalLayout');
      case 'kpi':
        return t('dashboardTemplateKpiCard');
      case 'bar':
        return t('dashboardTemplateBarChart');
      case 'pie':
        return t('dashboardTemplatePieChart');
      case 'table':
        return t('dashboardTemplateTable');
      default:
        return type || t('dashboardTemplateElement');
    }
  };

  const getBindingKey = (type) => {
    if (type === 'kpi') return 'amount';
    if (type === 'bar' || type === 'pie' || type === 'table') return 'data';
    return null;
  };

  const getFlatLayoutRows = (layoutNode, parent = null, level = 0, path = []) => {
    if (!layoutNode?.elements) return [];

    let rows = [];

    layoutNode.elements.forEach((element, index) => {
      const currentPath = [...path, index];

      rows.push({
        ...element,
        __level: level,
        __path: currentPath,
        __parentPath: path,
        __index: index,
        __isLayout: element.type?.includes('Layout')
      });

      if (element.type?.includes('Layout') && element.elements?.length) {
        rows = rows.concat(getFlatLayoutRows(element, element, level + 1, currentPath));
      }
    });

    return rows;
  };

  const getNodeByPath = (root, path = []) => {
    let current = root;
    for (let i = 0; i < path.length; i++) {
      if (!current?.elements || current.elements[path[i]] == null) return null;
      current = current.elements[path[i]];
    }
    return current;
  };

  const moveNodeAtPath = (parentPath, fromIndex, toIndex) => {
    const newTemplate = JSON.parse(JSON.stringify(template));
    const parentNode = parentPath.length === 0
      ? newTemplate.layout
      : getNodeByPath(newTemplate.layout, parentPath);

    if (!parentNode?.elements) return;
    if (fromIndex < 0 || fromIndex >= parentNode.elements.length) return;
    if (toIndex < 0 || toIndex >= parentNode.elements.length) return;
    if (fromIndex === toIndex) return;

    const items = parentNode.elements;
    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);

    setTemplate(newTemplate);
  };

  const updateNodeAtPath = (path, updater) => {
    const newTemplate = JSON.parse(JSON.stringify(template));
    const node = getNodeByPath(newTemplate.layout, path);
    if (!node) return;
    updater(node);
    setTemplate(newTemplate);
  };

  const deleteNodeAtPath = (parentPath, index) => {
    const newTemplate = JSON.parse(JSON.stringify(template));
    const parentNode = parentPath.length === 0
      ? newTemplate.layout
      : getNodeByPath(newTemplate.layout, parentPath);

    if (!parentNode?.elements) return;

    parentNode.elements.splice(index, 1);
    setTemplate(newTemplate);
  };

  const addElementAtPath = (targetPath, type) => {
    const newTemplate = JSON.parse(JSON.stringify(template));
    const parentNode = targetPath.length === 0
      ? newTemplate.layout
      : getNodeByPath(newTemplate.layout, targetPath);

    if (!parentNode) return;

    const isLayout = type.includes('Layout');
    const newElem = isLayout
      ? { type, elements: [] }
      : { type, title: t('dashboardTemplateNewElement'), icon: 'info', bindings: {} };

    if (!parentNode.elements) parentNode.elements = [];
    parentNode.elements.push(newElem);
    setTemplate(newTemplate);
  };

  const getSiblingCount = (parentPath) => {
    const parentNode = parentPath.length === 0
      ? template.layout
      : getNodeByPath(template.layout, parentPath);
    return parentNode?.elements?.length || 0;
  };

  const getLevelColor = (level) => {
    if (level === 0) return '#2563eb';
    if (level === 1) return '#7c3aed';
    if (level === 2) return '#0891b2';
    if (level === 3) return '#16a34a';
    return '#64748b';
  };

  const getRowClassName = (rowData) => {
    if (rowData.__isLayout) {
      return rowData.__level === 0 ? 'dashboard-template-layout-row-root' : 'dashboard-template-layout-row-nested';
    }
    return 'dashboard-template-element-row';
  };

  const renderEditableTable = (layoutNode) => {
    if (!layoutNode) return null;

    const rows = getFlatLayoutRows(layoutNode);

    return (
      <div>
        <style>{`
          .dashboard-template-structure-table .dashboard-template-layout-row-root > td {
            background: #f8fbff !important;
          }

          .dashboard-template-structure-table .dashboard-template-layout-row-nested > td {
            background: #fcfcfd !important;
          }

          .dashboard-template-structure-table .dashboard-template-element-row > td {
            background: #ffffff !important;
          }

          .dashboard-template-structure-table .p-datatable-tbody > tr:hover > td {
            background: #f8fafc !important;
          }
        `}</style>
        <div
          className="p-d-flex p-jc-between p-ai-center p-mb-3 p-p-3"
          style={{
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px'
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: '#495057' }}>{t('dashboardTemplateStructureEdit')}</div>
          </div>

          <div className="p-d-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button
              icon="pi pi-plus"
              label="KPI"
              className="p-button-sm p-button-outlined"
              onClick={() => addElementAtPath([], 'kpi')}
            />
            <Button
              icon="pi pi-chart-bar"
              label="Chart"
              className="p-button-sm p-button-outlined p-button-secondary"
              onClick={() => addElementAtPath([], 'bar')}
            />
            <Button
              icon="pi pi-sitemap"
              label="Layout"
              className="p-button-sm p-button-outlined p-button-help"
              onClick={() => addElementAtPath([], 'horizontalLayout')}
            />
          </div>
        </div>

        <DataTable
          value={rows}
          className="p-datatable-sm dashboard-template-structure-table"
          responsiveLayout="scroll"
          emptyMessage={t('dashboardTemplateEmptyLayout')}
          stripedRows
          rowClassName={getRowClassName}
        >
          <Column
            header={t('dashboardTemplateElement')}
            style={{ width: '320px' }}
            body={(rowData) => {
              const levelColor = getLevelColor(rowData.__level);
              const siblingCount = getSiblingCount(rowData.__parentPath);
              const isFirst = rowData.__index === 0;
              const isLast = rowData.__index === siblingCount - 1;

              return (
                <div
                  className="p-d-flex p-ai-center"
                  style={{
                    paddingLeft: `${rowData.__level * 28}px`,
                    gap: '0.75rem',
                    position: 'relative',
                    minHeight: '44px'
                  }}
                >
                  {rowData.__level > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${rowData.__level * 28 - 16}px`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#94a3b8',
                        fontSize: '0.95rem',
                        fontWeight: 700
                      }}
                    >
                      └
                    </div>
                  )}
                  

                  <div className="p-d-flex p-flex-column" style={{ gap: '2px' }}>
                    <Button
                      icon="pi pi-chevron-up"
                      className="p-button-rounded p-button-text p-button-sm"
                      tooltip={t('moveUp')}
                      disabled={isFirst}
                      onClick={() => moveNodeAtPath(rowData.__parentPath, rowData.__index, rowData.__index - 1)}
                    />
                    <Button
                      icon="pi pi-chevron-down"
                      className="p-button-rounded p-button-text p-button-sm"
                      tooltip={t('moveDown')}
                      disabled={isLast}
                      onClick={() => moveNodeAtPath(rowData.__parentPath, rowData.__index, rowData.__index + 1)}
                    />
                  </div>

                  <div
                    style={{
                      width: '4px',
                      alignSelf: 'stretch',
                      borderRadius: '4px',
                      background: levelColor,
                      opacity: 0.95
                    }}
                  />

                  <div
                    className="p-d-flex p-jc-center p-ai-center"
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: rowData.__isLayout ? '#eef2ff' : '#f8fafc',
                      color: levelColor,
                      border: `1px solid ${rowData.__isLayout ? '#c7d2fe' : '#e2e8f0'}`
                    }}
                  >
                    {rowData.__isLayout ? <i className="pi pi-sitemap" /> : <i className="pi pi-chart-bar" />}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div className="p-d-flex p-ai-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>
                        {rowData.title || getTypeLabel(rowData.type)}
                      </div>

                      <span
                        style={{
                          fontSize: '0.72rem',
                          padding: '2px 7px',
                          borderRadius: '999px',
                          background: '#f1f5f9',
                          color: '#475569',
                          border: '1px solid #e2e8f0',
                          fontWeight: 700
                        }}
                      >
                        {t('dashboardTemplateLevel', { level: rowData.__level })} 
                      </span>
                    </div>

                    <div className="p-mt-1">
                      {getTypeBadge(rowData.type)}
                    </div>
                  </div>
                </div>
              );
            }}
          />

          <Column
            header={t('columnType')}
            style={{ width: '240px' }}
            body={(rowData) => (
              <Dropdown
                value={rowData.type}
                options={typeOptions}
                onChange={(e) => {
                  updateNodeAtPath(rowData.__path, (node) => {
                    node.type = e.value;
                    if (e.value.includes('Layout') && !node.elements) node.elements = [];
                    if (!e.value.includes('Layout')) delete node.elements;
                  });
                }}
                className="w-full"
              />
            )}
          />

          <Column
            header={t('columnName')}
            body={(rowData) => {
              if (rowData.__isLayout) {
                return (
                  <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                    {t('dashboardTemplateLayoutElement')}
                  </div>
                );
              }

              return (
                <div className="p-grid">
                  <div className="p-col-12 p-lg-7 p-p-1">
                    <InputText
                      value={rowData.title || ''}
                      placeholder={t('dashboardTemplateElementTitle')}
                      onChange={(e) => {
                        updateNodeAtPath(rowData.__path, (node) => {
                          node.title = e.target.value;
                        });
                      }}
                    />
                  </div>
                  <div className="p-col-12 p-lg-5 p-p-1">
                    <InputText
                      value={rowData.icon || ''}
                      placeholder={t('iconPlaceholder')}
                      onChange={(e) => {
                        updateNodeAtPath(rowData.__path, (node) => {
                          node.icon = e.target.value;
                        });
                      }}
                    />
                  </div>
                </div>
              );
            }}
          />

          <Column
            header={t('columnBoundToType')}
            style={{ width: '280px' }}
            body={(rowData) => {
              if (rowData.__isLayout) {
                return <Badge value={t('dashboardTemplateLayoutBadge')} severity="info" />;
              }

              const bindingKey = getBindingKey(rowData.type);
              const options = rowData.type === 'kpi' ? availableElements.kpis : availableElements.charts;

              if (!bindingKey) {
                return <span style={{ color: '#6c757d' }}>{t('none')}</span>;
              }

              return (
                <Dropdown
                  value={rowData.bindings?.[bindingKey]}
                  options={options}
                  onChange={(e) => {
                    updateNodeAtPath(rowData.__path, (node) => {
                      if (!node.bindings) node.bindings = {};
                      node.bindings[bindingKey] = e.value;
                    });
                  }}
                  className="w-full"
                  placeholder={t('dashboardTemplateApiFieldPlaceholder')}
                  showClear
                />
              );
            }}
          />

          <Column
          header={t('columnActions')}
          style={{ width: '220px' }}
          body={(rowData) => {
            return (
              <div className="p-d-flex p-ai-center" style={{ gap: '0.35rem', flexWrap: 'wrap' }}>
                {rowData.__isLayout && (
                  <>
                    <Button
                      icon="pi pi-plus"
                      className="p-button-success"
                      tooltip={t('addKpi')}
                      onClick={() => addElementAtPath(rowData.__path, 'kpi')}
                    />
                    <Button
                      icon="pi pi-chart-bar"
                      className="p-button-secondary"
                      tooltip={t('addChart')}
                      onClick={() => addElementAtPath(rowData.__path, 'bar')}
                    />
                    <Button
                      icon="pi pi-sitemap"
                      className="p-button-help"
                      tooltip={t('addLayout')}
                      onClick={() => addElementAtPath(rowData.__path, 'horizontalLayout')}
                    />
                  </>
                )}

                <Button
                  icon="pi pi-trash"
                  className="p-button-danger"
                  tooltip={t('delete')}
                  onClick={() => {
                    if (window.confirm(t('dashboardTemplateConfirmDeleteElement'))) {
                      deleteNodeAtPath(rowData.__parentPath, rowData.__index);
                    }
                  }}
                />
              </div>
            );
          }}
        />
        </DataTable>
      </div>
    );
  };

  if (loading || !template) {
    return (
      <div className="dashboard-template-editor widget" style={{ position: 'relative', minHeight: '300px' }}>
        <ProgressBar
          mode="indeterminate"
          style={{ height: '5px', position: 'absolute', top: 0, left: 0, right: 0, borderRadius: 0 }}
        />
        <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '300px' }}>
          <div className="p-text-center">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#64748b' }} />
            <div className="p-mt-3" style={{ color: '#64748b' }}>{t('loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  const save = (goBack = false) => {
    setLoading(true);
    const method = action === 'create' ? 'post' : 'put';
    const url = action === 'create' ? '/api/v2/dashboard-template' : `/api/v2/dashboard-template/${id}`;
    apiCall(method, url, template)
      .then(() => {
        window.App.toastShow('success', t('toastSaveSuccess'));
        if (goBack) {
          history.push('/settings/dashboard-template');
        }
      })
      .catch(err => window.App.toastShow('error', t('error'), err.toString()))
      .finally(() => setLoading(false));
  };

  return (
    <div className="dashboard-template-editor widget" style={{ position: 'relative' }}>
      {loading && (
        <ProgressBar
          mode="indeterminate"
          style={{ height: '5px', position: 'absolute', top: 0, left: 0, right: 0, borderRadius: 0, zIndex: 2 }}
        />
      )}

      <div className="p-grid p-ai-start p-mb-3">
        <div className="p-col-12 p-lg-6 p-col-align-center p-text-center p-text-lg-left">
          <h2 className="p-m-0 p-p-0 text-primary p-d-inline">
            {action === 'create' ? t('dashboardTemplateCreate') : t('dashboardTemplateUpdate')}
          </h2>
        </div>
      </div>

      <Panel>
        <div className="p-grid">
          <div className="p-col-12 p-lg-6">
            <div className="p-field p-fluid">
              <label>{props.t('fieldName')}</label>
              <InputText
                value={template.name || ''}
                onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-6">
            <div className="p-field p-fluid">
              <label>{props.t('fieldCode')}</label>
              <InputText
                value={template.code || ''}
                onChange={(e) => setTemplate({ ...template, code: e.target.value })}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-6">
            <div className="p-field p-fluid">
              <label>{props.t('fieldOwnerType')}</label>
              <InputText
                value={template.owner_type || ''}
                onChange={(e) => setTemplate({ ...template, owner_type: e.target.value })}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-6">
            <div className="p-field p-fluid">
              <label>{props.t('fieldRefreshSeconds')}</label>
              <InputText
                type="number"
                value={template.cooldown_seconds}
                onChange={(e) => setTemplate({ ...template, cooldown_seconds: e.target.value })}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{props.t('fieldActive')}</label>
              <div className="field-control">
                <InputSwitch
                  checked={!!template.is_active}
                  onChange={(e) => setTemplate({ ...template, is_active: e.value })}
                />
              </div>
            </div>
          </div>
          <div className="p-col-12 p-lg-12 p-col-align-center p-text-center p-text-lg-right">
            <Button
              type="button"
              label={t('save')}
              loading={loading}
              icon="pi pi-save"
              className="p-button-success p-ml-2"
              onClick={() => save(false)}
            />
            <Button
              type="button"
              label={t('saveAndBack')}
              loading={loading}
              icon="pi pi-arrow-left"
              className="p-button-success p-ml-2"
              onClick={() => save(true)}
            />
          </div>
        </div>
      </Panel>
      

      {/* <div className="p-grid p-mt-3">
        <div className="p-col-12 p-lg-3">
          <Panel header="Elérhető layoutok">
            <div className="p-d-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              {availableElements.layouts?.map((item) => (
                <Badge key={item} value={item} severity="info" />
              ))}
            </div>
          </Panel>
        </div>

        <div className="p-col-12 p-lg-3">
          <Panel header="Elérhető chartok">
            <div className="p-d-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              {availableElements.charts?.map((item) => (
                <Badge key={item} value={item} severity="warning" />
              ))}
            </div>
          </Panel>
        </div>

        <div className="p-col-12 p-lg-3">
          <Panel header="Elérhető KPI-k">
            <div className="p-d-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              {availableElements.kpis?.map((item) => (
                <Badge key={item} value={item} severity="success" />
              ))}
            </div>
          </Panel>
        </div>

        <div className="p-col-12 p-lg-3">
          <Panel header="Gyors műveletek">
            <div className="p-d-flex p-flex-column" style={{ gap: '0.5rem' }}>
              <Button
                icon="pi pi-plus"
                label="KPI hozzáadása"
                className="p-button-sm"
                onClick={() => addElementAtPath([], 'kpi')}
              />
              <Button
                icon="pi pi-chart-pie"
                label="Chart hozzáadása"
                className="p-button-sm p-button-secondary"
                onClick={() => addElementAtPath([], 'pie')}
              />
              <Button
                icon="pi pi-sitemap"
                label="Layout hozzáadása"
                className="p-button-sm p-button-help"
                onClick={() => addElementAtPath([], 'horizontalLayout')}
              />
            </div>
          </Panel>
        </div>
      </div> */}

      <div className="p-grid p-mt-3">
        <div className="p-col-12">
          {renderEditableTable(template.layout)}
        </div>
      </div>
    </div>
  );
};

export default withTranslation('common')(DashboardTemplateFormComponent);
